import React, { useEffect, Suspense, lazy, createElement } from 'react';
import { SocialProof } from './components/SocialProof';
import { PainPoints } from './components/PainPoints';
import { ProductShowcase } from './components/ProductShowcase';
const WhatYouGet = lazy(() => import('./components/WhatYouGet').then(m => ({
  default: m.WhatYouGet
})));
const Bonuses = lazy(() => import('./components/Bonuses').then(m => ({
  default: m.Bonuses
})));
const Countdown = lazy(() => import('./components/Countdown').then(m => ({
  default: m.Countdown
})));
const Pricing = lazy(() => import('./components/Pricing').then(m => ({
  default: m.Pricing
})));
const ProBusiness = lazy(() => import('./components/ProBusiness').then(m => ({
  default: m.ProBusiness
})));
const Guarantee = lazy(() => import('./components/Guarantee').then(m => ({
  default: m.Guarantee
})));
const Testimonials = lazy(() => import('./components/Testimonials').then(m => ({
  default: m.Testimonials
})));
const FAQ = lazy(() => import('./components/FAQ').then(m => ({
  default: m.FAQ
})));
const DarkCTA = lazy(() => import('./components/DarkCTA').then(m => ({
  default: m.DarkCTA
})));
const Footer = lazy(() => import('./components/Footer').then(m => ({
  default: m.Footer
})));
const PurchaseNotifications = lazy(() => import('./components/PurchaseNotifications').then(m => ({
  default: m.PurchaseNotifications
})));
// Deferred Facebook Pixel loader
function loadFbPixel() {
  if (typeof window === 'undefined' || (window as any).fbq) return;
  const f = window as any;
  const n = f.fbq = function () {
    n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
  };
  if (!f._fbq) f._fbq = n;
  n.push = n;
  n.loaded = true;
  n.version = '2.0';
  n.queue = [];
  (window as any).fbq('init', '1215304437119773');
  (window as any).fbq('track', 'PageView');
  const t = document.createElement('script');
  t.async = true;
  t.src = 'https://connect.facebook.net/en_US/fbevents.js';
  const s = document.getElementsByTagName('script')[0];
  s?.parentNode?.insertBefore(t, s);
}
export function App() {
  useEffect(() => {
    document.title = 'Megapack Artes Marciales — El Sistema Más Completo para Instructores de Argentina';
    document.documentElement.lang = 'es';
    const setMeta = (name: string, content: string, property?: boolean) => {
      const attr = property ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.content = content;
    };
    const description = 'El sistema más completo de Argentina para instructores de artes marciales. 6 Tomos + 6 Bonos de gestión, planificación, retención de alumnos y escalabilidad del dojo. Acceso inmediato en PDF.';
    const url = 'https://artesmarciales.construyeprogreso.com';
    const image = 'https://cdn.magicpatterns.com/uploads/v7HbDBttB6KwqMN8KX8fXz/megapack-artes-marciales-mockup.webp';
    setMeta('description', description);
    setMeta('keywords', 'megapack artes marciales, sistema para instructores, planificación de clases, retención de alumnos, gestión de dojo, marketing marcial, Argentina, construye progreso');
    setMeta('robots', 'index, follow');
    setMeta('author', 'Construye Progreso™');
    setMeta('viewport', 'width=device-width, initial-scale=1');
    if (!document.querySelector('link[rel="canonical"]')) {
      const canonical = document.createElement('link');
      canonical.rel = 'canonical';
      canonical.href = url;
      document.head.appendChild(canonical);
    }
    setMeta('og:title', 'Megapack Artes Marciales — El Sistema Más Completo para Instructores de Argentina', true);
    setMeta('og:description', description, true);
    setMeta('og:image', image, true);
    setMeta('og:url', url, true);
    setMeta('og:type', 'product', true);
    setMeta('og:locale', 'es_AR', true);
    setMeta('og:site_name', 'Construye Progreso™', true);
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', 'Megapack Artes Marciales — Construye Progreso™');
    setMeta('twitter:description', description);
    setMeta('twitter:image', image);
    if (!document.querySelector('script[data-ld="product"]')) {
      const ld = document.createElement('script');
      ld.type = 'application/ld+json';
      ld.setAttribute('data-ld', 'product');
      ld.textContent = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: 'Megapack Artes Marciales — Sistema Completo para Instructores',
        description: description,
        image: image,
        brand: {
          '@type': 'Brand',
          name: 'Construye Progreso™'
        },
        offers: {
          '@type': 'AggregateOffer',
          lowPrice: '19990',
          highPrice: '27990',
          priceCurrency: 'ARS',
          availability: 'https://schema.org/InStock',
          offerCount: 2
        },
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: '4.9',
          reviewCount: '847'
        }
      });
      document.head.appendChild(ld);
    }
    if (!document.querySelector('script[data-ld="org"]')) {
      const orgLd = document.createElement('script');
      orgLd.type = 'application/ld+json';
      orgLd.setAttribute('data-ld', 'org');
      orgLd.textContent = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'Construye Progreso™',
        url: url,
        logo: image,
        email: 'construyeprogresoo@gmail.com',
        sameAs: ['https://www.instagram.com/construyeprogresoo']
      });
      document.head.appendChild(orgLd);
    }
    if (!document.querySelector('script[data-ld="faq"]')) {
      const faqLd = document.createElement('script');
      faqLd.type = 'application/ld+json';
      faqLd.setAttribute('data-ld', 'faq');
      faqLd.textContent = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [{
          '@type': 'Question',
          name: '¿Qué incluye el Megapack Artes Marciales?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: '6 Tomos completos de 60 páginas cada uno sobre gestión, retención, marketing, planificación técnica, ventas y liderazgo. Más 6 Bonos de implementación inmediata con contratos, agenda anual, scripts, planillas y calendario editorial.'
          }
        }, {
          '@type': 'Question',
          name: '¿Cómo recibo el material?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Todo es 100% digital en formato PDF. Recibís acceso inmediato en tu email dentro de los 5 minutos posteriores a la compra.'
          }
        }, {
          '@type': 'Question',
          name: '¿Tiene garantía?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Sí, ofrecemos garantía de 7 días sin preguntas. Si no te sirve, te devolvemos el 100% de tu dinero.'
          }
        }, {
          '@type': 'Question',
          name: '¿Para qué tipo de instructor es el Megapack?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'El Megapack Artes Marciales está diseñado para instructores y dueños de dojos de cualquier disciplina marcial en Argentina que quieran profesionalizar su academia, retener más alumnos y aumentar sus ingresos.'
          }
        }]
      });
      document.head.appendChild(faqLd);
    }
  }, []);
  // Defer FB pixel until after initial paint
  useEffect(() => {
    if ('requestIdleCallback' in window) {
      ;
      (window as any).requestIdleCallback(loadFbPixel, {
        timeout: 3000
      });
    } else {
      setTimeout(loadFbPixel, 2000);
    }
  }, []);
  return <>
      <noscript data-id="element-0">
        <img height="1" width="1" style={{
        display: 'none'
      }} src="https://www.facebook.com/tr?id=1215304437119773&ev=PageView&noscript=1" alt="" data-id="element-1" />
      </noscript>

      <header className="relative flex w-full flex-col items-center justify-center text-center text-gray-900 text-base leading-6 px-4 sm:px-5 py-8 sm:py-12" role="banner" style={{
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      backgroundImage: 'linear-gradient(135deg, rgb(10,10,10) 0%, rgb(30,10,10) 30%, rgb(10,10,10) 60%, rgb(20,0,0) 100%)',
      backgroundColor: 'rgb(10, 10, 10)'
    }} data-id="element-2">
        {/* MARCA */}
        <div className="mb-2" data-id="element-3">
          <span className="text-xl sm:text-2xl font-black tracking-[2.4px] text-red-500" data-id="element-4">
            🥋 CONSTRUYE PROGRESO™
          </span>
        </div>

        {/* BADGE DE URGENCIA */}
        <div className="mb-3 bg-red-600 text-white text-xs sm:text-sm font-black px-4 py-1.5 rounded-full animate-pulse" data-id="element-5">
          🔥 OFERTA POR TIEMPO LIMITADO — 60% OFF HOY
        </div>

        {/* HEADLINE PRINCIPAL */}
        <h1 className="text-[36px] leading-[42px] sm:text-5xl sm:leading-tight md:text-[72px] md:leading-[80px] font-black max-w-4xl text-white m-0 mb-4" data-id="element-6">
          Todo tu año de
          <br data-id="element-7" />
          <span className="text-red-500" data-id="element-8">Artes Marciales</span>
          <br data-id="element-9" />
          resuelto.
        </h1>

        {/* IMAGEN DEL PRODUCTO — LCP element */}
        <div className="my-6 w-full max-w-[360px] sm:max-w-[400px] aspect-square relative" style={{
        perspective: '800px'
      }} data-id="element-10">
          <div className="absolute inset-4 rounded-full blur-3xl opacity-40 -z-10" style={{
          backgroundColor: 'rgb(196, 30, 58)'
        }} data-id="element-11" />
          <img src="https://cdn.magicpatterns.com/uploads/1oGQeRLN1m8EVFzXgb5m6U/Gemini_Generated_Image_4czaxo4czaxo4cza-Photoroom.webp" alt="Megapack Artes Marciales — Sistema Completo para Instructores — Construye Progreso" width="400" height="400" sizes="(max-width: 640px) 360px, 400px" fetchPriority="high" decoding="sync" className="block max-w-full w-full h-auto object-contain relative z-10 drop-shadow-2xl" style={{
          transform: 'rotateY(-4deg) rotateX(2deg)',
          transformStyle: 'preserve-3d'
        }} data-id="element-12" />
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-3/4 h-6 rounded-full blur-xl opacity-20" style={{
          backgroundColor: 'rgb(196, 30, 58)'
        }} data-id="element-13" />
        </div>

        {/* PROMESA EMOCIONAL */}
        <p className="max-w-3xl sm:text-xl font-bold text-red-400 m-0 mb-3 text-[16px]" data-id="element-14">
          "Listo para implementar. Sin improvisar más. Sin perder horas. Sin
          perder alumnos."
        </p>

        {/* DESCRIPCIÓN DEL SISTEMA */}
        <p className="max-w-2xl text-sm sm:text-base text-gray-400 m-0 mb-5 leading-relaxed" data-id="element-15">
          El sistema para instructores de artes marciales en Argentina: año
          completo resuelto (clases + técnica + retención + marketing + cobros +
          gestión + academia online), sin buscar ni armar nada desde cero.
        </p>

        {/* TAGS DE CONFIANZA */}
        <div className="mb-5 grid grid-cols-2 sm:grid-cols-4 gap-2 max-w-lg" data-id="element-16">
          {['📄 PDF Digital', '⚡ Acceso Ya', '🇦🇷 Argentina', '🥋 Toda disciplina', '✅ Completo', '📋 Plan Anual', '💰 +Retención', '🎯 Gestión 360°'].map(tag => <span key={tag} className="bg-gray-900/80 text-xs font-semibold text-gray-200 border border-red-900/40 rounded-full px-3 py-1.5 text-center" data-id="element-17">
              {tag}
            </span>)}
        </div>

        {/* PRECIO TACHADO */}

        {/* CTA PRINCIPAL */}
        <a href="#pricing" onClick={() => {
        if (typeof window !== 'undefined' && (window as any).fbq) {
          ;
          (window as any).fbq('track', 'Lead', {
            content_name: 'Hero CTA Click',
            content_category: 'Artes Marciales'
          });
        }
      }} className="block w-full max-w-sm bg-red-600 hover:bg-red-700 text-white sm:text-xl font-black rounded-xl px-6 sm:px-8 py-4 mb-4 no-underline transition-colors text-center shadow-lg text-[20px]" data-id="element-18">
          🥋 QUIERO EL MEGAPACK AHORA
        </a>

        {/* GARANTÍAS */}
        <p className="flex flex-col sm:flex-row flex-wrap items-center justify-center gap-2 sm:gap-3 text-xs sm:text-sm text-gray-500 m-0" data-id="element-19">
          <span data-id="element-20">🛡️ Garantía 7 días sin preguntas</span>
          <span data-id="element-21">🔒 Pago 100% seguro</span>
          <span data-id="element-22">💳 Mercado Pago | Tarjeta | Transferencia</span>
        </p>
      </header>

      <main data-id="element-23">
        <SocialProof data-id="element-24" />
        <PainPoints data-id="element-25" />
        <ProductShowcase data-id="element-26" />

        <Suspense fallback={null} data-id="element-27">
          <WhatYouGet data-id="element-28" />
          <Bonuses data-id="element-29" />
          <Countdown data-id="element-30" />
          <Pricing data-id="element-31" />
          <ProBusiness data-id="element-32" />
          <Guarantee data-id="element-33" />
          <Testimonials data-id="element-34" />
          <FAQ data-id="element-35" />
          <DarkCTA data-id="element-36" />
        </Suspense>
      </main>

      <Suspense fallback={null} data-id="element-37">
        <Footer data-id="element-38" />
        <PurchaseNotifications data-id="element-39" />
      </Suspense>
    </>;
}